import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { SITE_BASE_URL } from '../../config/environments/api';
import './AboutPage.scss';

const AboutPage = () => {
  const location = useLocation();
  const previousHashRef = useRef('');
  const scrollTimeoutRef = useRef(null);

  // Handle smooth scroll to partner section when hash is present
  useEffect(() => {
    const scrollToElement = (elementId, retryCount = 0) => {
      // Clear any existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = null;
      }

      const element = document.getElementById(elementId);
      
      if (element) {
        // Calculate header offset (header is fixed, main-content has margin-top: 83px)
        const isMobile = window.innerWidth <= 768;
        const headerOffset = isMobile ? 130 : 110; // Increased offset for better visibility on mobile
        
        // Get element position - recalculate to get current position
        const elementPosition = element.getBoundingClientRect().top;
        const currentScrollY = window.pageYOffset || document.documentElement.scrollTop;
        const offsetPosition = currentScrollY + elementPosition - headerOffset;
        
        // Ensure we don't scroll to negative position
        const finalPosition = Math.max(0, offsetPosition);
        
        // Scroll to position with offset
        window.scrollTo({
          top: finalPosition,
          behavior: 'smooth'
        });
        return true;
      } else if (retryCount < 3) {
        // Retry if element not found (for page navigation or slow rendering)
        scrollTimeoutRef.current = setTimeout(() => {
          scrollToElement(elementId, retryCount + 1);
        }, retryCount === 0 ? 100 : retryCount === 1 ? 200 : 300);
      }
      return false;
    };

    // Function to handle hash scrolling
    const handleHashScroll = (hash) => {
      if (!hash) return;
      
      const elementId = hash.substring(1);
      if (elementId && elementId !== previousHashRef.current) {
        previousHashRef.current = elementId;
        scrollToElement(elementId);
      } else if (elementId && elementId === previousHashRef.current) {
        // Even if hash is the same, scroll again (for repeated clicks)
        scrollToElement(elementId);
      }
    };

    // Get current hash from window.location (most reliable)
    const currentHash = window.location.hash || location.hash;
    
    // Always check and scroll if hash exists
    if (currentHash) {
      handleHashScroll(currentHash);
    }

    // Listen for hashchange events (catches same-page link clicks)
    const handleHashChange = () => {
      const newHash = window.location.hash;
      if (newHash) {
        handleHashScroll(newHash);
      }
    };

    // Also listen for popstate (browser back/forward)
    const handlePopState = () => {
      setTimeout(() => {
        const hash = window.location.hash;
        if (hash) {
          handleHashScroll(hash);
        }
      }, 50);
    };

    window.addEventListener('hashchange', handleHashChange);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
      window.removeEventListener('popstate', handlePopState);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = null;
      }
    };
  }, [location.hash, location.pathname]);

  return (
    <>
      <Helmet>
        <title>Africa Energy Trends | Mission & Partners</title>
        <meta property="og:title" content="Africa Energy Trends | Mission & Partners" />
        <meta property="og:description" content="Learn about Africa Energy Trends, its mission, partners, & commitment to transparent energy, data-driven insights that support sustainable energy." />
        <meta property="og:image" content={`${SITE_BASE_URL}/og-images/about.png`} />
        <meta property="og:image:secure_url" content={`${SITE_BASE_URL}/og-images/about.png`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:url" content={`${SITE_BASE_URL}/about`} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Africa Energy Access Trends" />
        <meta name="description" content="Learn about Africa Energy Trends, its mission, partners, & commitment to transparent energy, data-driven insights that support sustainable energy." />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Africa Energy Trends | Mission & Partners" />
        <meta name="twitter:description" content="Learn about Africa Energy Trends, its mission, partners, & commitment to transparent energy, data-driven insights that support sustainable energy." />
        <meta name="twitter:image" content={`${SITE_BASE_URL}/og-images/about.png`} />
      </Helmet>
      <div className="about-page">
      {/* About section */}
      <section className="about-energy-tracker-section">
        <div className="container">
          <div className="about-energy-tracker-grid">
            <div className="about-tracker-card">
              <h2 className="card-title">About the Tracker</h2>
              <div className="card-content">
                <p>
                 The Africa SDG 7 Investment and Progress Tracker is a joint initiative of Vasudha Foundation, the Access coalition and Steer...... in partnership with CS Mott Foundation. 
                 The primary intent of this portal is to track investment flows towards energy access projects in Africa with a focus on Investments from the African Development Bank and the World Bank Group and also to track the progress on energy access across Africa through these investments.  
                </p>
                <p>
                The tracker is designed to track year on year investments and progress on energy access and also the impacts of the energy access projects on communities and economies. 
                In addition to quantitative information the tracker would also attempt to track qualitative information for ensuring universal access to affordable, reliable and modern energy services by 2030.
                </p>
              </div>
            </div>

            <div className="why-tracker-card">
              <h2 className="card-title">Why the Tracker</h2>
              <h3 className='card-subtitle'>Data-Driven Energy Access for African Stakeholder</h3>
              <div className="card-content">
                <p>
                An Africa SDG 7 Investment & Progress Tracker is crucial for all stakeholders in Africa because it enables evidence-based decision-making, transparency, and collaboration across governments, investors, utilities, and communities. Such a database consolidates fragmented data and makes it accessible and reliable, supporting the efficient planning, allocation, and monitoring of energy interventions at both national and local levels.  
                </p>
                <p>
                For policymakers, it helps identify gaps and track progress toward universal access and climate goals.  
                </p>
                <p>For the private sector and investors, it minimizes risks by highlighting opportunities and assessing project viability.  </p>
                <p>Development partners can use the data to coordinate funding and maximize impact, while local communities benefit from improved accountability and the ability to advocate for their needs. Ultimately, a robust tracking platform accelerates access expansion and ensures investments are aligned with Africa’s sustainable development priorities. </p>
                <p>This is a live dashboard with frequent updates on investment flows and progress on energy access. </p>
              </div>
            </div>

            <div className="partners-collaborations-card">
              <h2 className="card-title">Partners</h2>
              <div className="partners-card" id="vasudha-foundation">
                <h3 className='card-subtitle'>
                  <a href="https://vasudhausa.org/" target="_blank" rel="noopener noreferrer" className="partner-link">
                    Vasudha Foundation USA INC
                  </a>
                </h3>
                <div className="card-content">
                  <div className="content">
                    <div className="text">
                    <p>
                    Vasudha Foundation USA INC was set up in 2014 with the sole purpose of bringing in Southern Voice to Global Policy Making and with the specific intent of engaging actively with the Multilateral Development Institutions with a very specific focus on the World Bank Group (World Bank and IFC) in framing policies that would help in diversifying the portfolios of the World Bank Group to facilitate Developing Economies to address Climate Change and usher in Energy Transition in their respective economies. 
                    </p>
                    <div className="link">
                      <a href="https://vasudhausa.org/" target="_blank" rel="noopener noreferrer" className="partner-contact-link">
                        <img src="/images/contact-us/web-link.png" alt="Website" className="contact-icon" />
                        <span>vasudhausa.org</span>
                      </a>
                      <a href="mailto:info@vasudhausa.org" className="partner-contact-link">
                        <img src="/images/contact-us/mail.png" alt="Email" className="contact-icon" />
                        <span>info@vasudhausa.org</span>
                      </a>
                    </div>
                    </div>
                    <a href="https://vasudhausa.org/" target="_blank" rel="noopener noreferrer" className="partner-link">
                    <img src="/images/partners/vasudha-usa-logo.png" alt="Vasudha Foundation USA logo" className="partner-logo-small" />
                    </a>
                </div>
                </div>
              </div>
              <div className="partners-card" id="access-coalition">
              <h3 className='card-subtitle'>
                <a href="https://access-coalition.org/" target="_blank" rel="noopener noreferrer" className="partner-link">
                  ACCESS COALITION
                </a>
              </h3>
              <div className="card-content">
              <div className="content">
              <div className="text">
                <p>
                The ACCESS Coalition (Alliance of Civil Society Organisations for Clean Energy Access) is a global network of over 100 organisations working to end energy poverty. Established in 2014 and headquartered in Nairobi, Kenya, ACCESS unites civil society voices from Africa and Asia to ensure that clean energy solutions reach those most often left behind. 
                </p>
                <p>
                Its mission is to secure safe, reliable, affordable, and sustainable energy for people living in poverty, guided by values of equity, inclusivity, accountability, and climate responsibility. ACCESS goal is to ensure clean energy investments reach the 600 million Africans without electricity and 900 million without clean cooking, through advocacy, accountability and community centred solutions. 
                </p>
                <p>ACCESS drives investment in distributed renewable energy and clean cooking, strengthens community participation in decision-making, and integrates energy with broader development priorities like health, education, and women's empowerment. A core part of its work is engaging multilateral development banks (MDBs) such as the World Bank and African Development Bank to redirect financing from fossil fuels toward people-centred, climate-smart solutions, ensuring last-mile communities, women, and youth benefit from global energy investments. </p>
                <p>This tracker reflects ACCESS commitment to transparency and accountability in clean energy investment, helping partners monitor progress toward universal energy access and ensuring investments reach the communities that need most </p>
                <div className="link">
                  <a href="https://access-coalition.org/" target="_blank" rel="noopener noreferrer" className="partner-contact-link">
                    <img src="/images/contact-us/web-link.png" alt="Website" className="contact-icon" />
                    <span>access-coalition.org</span>
                  </a>
                  <a href="mailto:info@access-coalition.org" className="partner-contact-link">
                    <img src="/images/contact-us/mail.png" alt="Email" className="contact-icon" />
                    <span>info@access-coalition.org</span>
                  </a>
                </div>
              </div>
              <a href="https://access-coalition.org/" target="_blank" rel="noopener noreferrer" className="partner-link">
                  <img src="/images/navbar-logo/access-coalition.png" alt="ACCESS Coalition logo" className="partner-logo-small" />
              </a>
              </div>
              </div> 
              </div>   
              <div className='partners-card' id="steer">
                <h3 className='card-subtitle'>
                  <a href="https://www.lboro.ac.uk/research/steer/" target="_blank" rel="noopener noreferrer" className="partner-link">
                    STEER – Centre for Sustainable Transitions: Energy, Environment and Resilience)
                  </a>
                </h3>
                <div className="card-content">
                <div className="content">
                <div className="text">
                  <p>
                  The Centre for Sustainable Transitions: Energy, Environment and Resilience (STEER) is a leading research centre committed to accelerating the transition to inclusive, sustainable and resilient energy systems through innovative research, analysis and capacity building. 
                  </p>
                  <div className="link">
                    <a href="https://www.lboro.ac.uk/research/steer/" target="_blank" rel="noopener noreferrer" className="partner-contact-link">
                      <img src="/images/contact-us/web-link.png" alt="Website" className="contact-icon" />
                      <span>www.lboro.ac.uk/research/steer/</span>
                    </a>
                    <a href="mailto:steer@lboro.ac.uk" className="partner-contact-link">
                      <img src="/images/contact-us/mail.png" alt="Email" className="contact-icon" />
                      <span>steer@lboro.ac.uk</span>
                    </a>
                  </div>
                </div>
                  <a href="https://www.lboro.ac.uk/research/steer/" target="_blank" rel="noopener noreferrer" className="partner-link">
                  <img src="/images/navbar-logo/steer-logo.jpg" alt="STEER energy logo" className="partner-logo-small" />
                  </a>
                </div>
                </div>
              </div>
              <div className='partners-card' id="vasudha-environmental">
                <h3 className='card-subtitle'>
                  Vasudha Environmental Services
                </h3>
                <div className="card-content">
                <div className="content">
                  <div className="text">
                  <p>
                  Vasudha Environmental Services, New Delhi India is a boutique consulting firm in the space of clean energy access.
                  </p>
                  </div>
                  <img src="/images/navbar-logo/vasudha-environmental-logo.jpg" alt="Vasudha Environmental Services logo" className="enviro-logo partner-logo-small" />
                  </div>
                </div>
              </div>
              <div className='partners-card' id="vistaar-digital">
                <h3 className='card-subtitle'>
                  <a href="https://vistaardigital.com/" target="_blank" rel="noopener noreferrer" className="partner-link">
                    Vistaar Digital
                  </a>
                </h3>
                <div className="card-content">
                <div className="content">
                  <div className="text">
                  <p>
                  Vistaar Digital, founded in 2014, is a digital transformation firm committed to enabling purpose-driven organisations through innovative, scalable, and data-centric technology solutions. With expertise spanning AI-driven analytics, mobile and web platforms, and digital engagement tools, Vistaar supports organisations working towards sustainable development, public health, and environmental advocacy. Vistaar has worked with key government and private sector stakeholders, including NITI Aayog, Kerala State Government, and leading pharmaceutical companies such as Abbott Healthcare, Cipla, and Ajanta Pharma. Their projects aim to enhance decision-making, outreach, and behavioral change through digital means that serve both people and the planet.
                  </p>
                    <div className="link">
                    <a href="https://vistaardigital.com/" target="_blank" rel="noopener noreferrer" className="partner-contact-link">
                      <img src="/images/contact-us/web-link.png" alt="Website" className="contact-icon" />
                      <span>vistaardigital.com</span>
                    </a>
                    <a href="mailto:contact@vistaardigital.com" className="partner-contact-link">
                      <img src="/images/contact-us/mail.png" alt="Email" className="contact-icon" />
                      <span>contact@vistaardigital.com</span>
                    </a>
                  </div>
                  </div>
                  <a href="https://vistaardigital.com/" target="_blank" rel="noopener noreferrer" className="partner-link">
                    <img src="/images/partners/vistaar-logo.png" alt="Vistaar Digital" className="partner-logo-small" />
                  </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
    </>
  );
};

export default AboutPage;
